/**
 * Lightweight Vietnamese-aware gibberish / nonsense detection.
 *
 * Strategy:
 *  A) Short-message gate — if the message is very short (< 5 chars after
 *     cleanup), check against a whitelist of known short Vietnamese & English
 *     tokens.  If not whitelisted → gibberish.
 *  B) Pattern rules for longer messages:
 *     1. Word too long (> 12 alpha chars)
 *     2. Consonant streak ≥ 5
 *     3. Vowel streak ≥ 5
 *     4. Repeated character ≥ 4
 *     5. Keyboard-row run ≥ 5
 *     6. Foreign-letter (F/J/W/Z) density > 30 %
 *
 * URLs, emails, and numbers are stripped before checking.
 */

// ── Vietnamese vowels (NFC) ──
const VI_VOWELS = new Set([
  'a','ă','â','e','ê','i','o','ô','ơ','u','ư','y',
  'à','ả','ã','á','ạ','ằ','ẳ','ẵ','ắ','ặ',
  'ầ','ẩ','ẫ','ấ','ậ','è','ẻ','ẽ','é','ẹ',
  'ề','ể','ễ','ế','ệ','ì','ỉ','ĩ','í','ị',
  'ò','ỏ','õ','ó','ọ','ồ','ổ','ỗ','ố','ộ',
  'ờ','ở','ỡ','ớ','ợ','ù','ủ','ũ','ú','ụ',
  'ừ','ử','ữ','ứ','ự','ỳ','ỷ','ỹ','ý','ỵ',
]);

// ── Short words that are valid (Vietnamese + common English) ──
// Covers greetings, confirmations, questions, common verbs, etc.
const SHORT_WHITELIST = new Set([
  // Vietnamese 1-4 letter words
  'ừ','ờ','ơi','ơ','ạ','à','á','ủa','ê','ế',
  'có','là','và','đi','về','ra','vào','ở','do','vì',
  'gì','sao','nào','đâu','ai','này','kia','đó','đây',
  'tôi','bạn','em','anh','chị','ông','bà','con','mẹ',
  'hi','hey','ok','yes','no','yep','nah','bye','thx',
  'xin','dạ','vâng','rồi','đúng','sai','cảm','ơn',
  'tìm','giúp','mở','đóng','lưu','xóa','sửa','thêm',
  'hỏi','nói','viết','đọc','xem','cho','lấy','đưa',
  'hãy','hả','nhé','nhỉ','nha','hen','hé','alo',
  'sum','avg','max','min','help','edit','add','del',
  'chào','hello','alo','helo',
  'tổng','doanh','thu','lợi','chi','phí','bán','mua',
  'cộng','trừ','nhân','chia','tính','đếm',
]);

// ── QWERTY rows ──
const KB_ROWS = ['qwertyuiop','asdfghjkl','zxcvbnm'];

function isVowel(ch: string): boolean { return VI_VOWELS.has(ch); }

/** Strip URLs, emails, numbers. */
function sanitise(text: string): string {
  return text
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\S+@\S+\.\S+/g, '')
    .replace(/\b[0-9][0-9.,]*\b/g, '')
    .trim();
}

/** 5+ consecutive chars on a single QWERTY row. */
function hasKeyboardRun(word: string): boolean {
  const lower = word.toLowerCase();
  for (const row of KB_ROWS) {
    let run = 0;
    for (const ch of lower) {
      run = row.includes(ch) ? run + 1 : 0;
      if (run >= 5) return true;
    }
  }
  return false;
}

/** Check if a single word looks like gibberish. */
function isWordGibberish(word: string): boolean {
  const lower = word.toLowerCase();
  const alpha = lower.replace(/[^a-záàảãạăắằẳẵặâấầẩẫậéèẻẽẹêếềểễệíìỉĩịóòỏõọôốồổỗộơớờởỡợúùủũụưứừửữựýỳỷỹỵđ]/g, '');
  if (alpha.length < 2) return false;

  // Rule 1: word too long for Vietnamese
  if (alpha.length > 12) return true;

  // Rule 2: consonant streak ≥ 5
  let streak = 0;
  for (const ch of alpha) {
    streak = (!isVowel(ch)) ? streak + 1 : 0;
    if (streak >= 5) return true;
  }

  // Rule 3: vowel streak ≥ 5
  streak = 0;
  for (const ch of alpha) {
    streak = isVowel(ch) ? streak + 1 : 0;
    if (streak >= 5) return true;
  }

  // Rule 4: repeated character ≥ 4
  if (/(.)\1{3,}/u.test(lower)) return true;

  // Rule 5: keyboard row run
  if (hasKeyboardRun(alpha)) return true;

  return false;
}

/**
 * Detect whether the input text is gibberish / nonsensical.
 * Returns `true` → block the message; `false` → send to LLM.
 */
export function isGibberish(text: string): boolean {
  const clean = sanitise(text).normalize('NFC');
  if (clean.length === 0) return true; // empty after cleanup

  // ── A) Short-message gate ──
  // For very brief inputs, check against known valid short words.
  const words = clean.split(/\s+/).filter(Boolean);

  if (clean.length <= 5) {
    // If ALL words are in the whitelist → valid.  Otherwise → gibberish.
    const allKnown = words.every(w => {
      const lower = w.toLowerCase().replace(/[?!.,;:\u2026]+$/g, ''); // strip trailing punctuation
      return SHORT_WHITELIST.has(lower);
    });
    return !allKnown;
  }

  // ── B) Pattern rules for longer messages ──
  let flagCount = 0;
  let checkedCount = 0;

  for (const word of words) {
    const lower = word.toLowerCase();
    const alpha = lower.replace(/[^a-záàảãạăắằẳẵặâấầẩẫậéèẻẽẹêếềểễệíìỉĩịóòỏõọôốồổỗộơớờởỡợúùủũụưứừửữựýỳỷỹỵđ]/g, '');
    if (alpha.length < 2) continue;

    checkedCount++;
    if (isWordGibberish(word)) flagCount++;
  }

  // Rule 6: foreign letter density (F/J/W/Z)
  const allAlpha = clean.toLowerCase().replace(/[^a-záàảãạăắằẳẵặâấầẩẫậéèẻẽẹêếềểễệíìỉĩịóòỏõọôốồổỗộơớờởỡợúùủũụưứừửữựýỳỷỹỵđ]/g, '');
  if (allAlpha.length > 4) {
    const foreignCount = [...allAlpha].filter(ch => 'fjwz'.includes(ch)).length;
    if (foreignCount / allAlpha.length > 0.3) return true;
  }

  if (checkedCount === 0) return false;
  return flagCount / checkedCount > 0.5;
}
